import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import { PublicNavbar, PublicFooter } from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MapPin, Star, Briefcase, Globe, ChevronRight,
  Lock, Building2, Users, Award, Filter, X, ArrowRight, Map, List, BarChart2, Check, Bookmark
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const SearchMap = lazy(() => import('../components/SearchMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_SUGGESTIONS = [
  'Exhibition Stand Design', 'Stand Construction', 'Modular Stands',
  'Custom Booth Build', 'Booth Setup & Dismantling', 'Furniture Rental',
  'Graphic Printing', 'AV & Lighting', 'Storage & Transport',
  'Double-Decker Stands', 'Outdoor Stands', 'Pavilion Design'
];

export default function PublicFindContractors() {
  const [searchParams] = useSearchParams();
  const { city: cityParam, fairSlug } = useParams(); // For SEO routes
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // Convert slug to readable name: "hannover-messe" → "Hannover Messe"
  const fairFromSlug = fairSlug
    ? fairSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '';

  const [service, setService] = useState(searchParams.get('service') || '');
  const [city, setCity] = useState(cityParam ? decodeURIComponent(cityParam) : (searchParams.get('city') || ''));
  const [fair, setFair] = useState(fairFromSlug || searchParams.get('fair') || '');
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [viewMode, setViewMode] = useState('list');

  const [contractors, setContractors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [compareList, setCompareList] = useState([]); // slugs to compare
  const [featuredFairs, setFeaturedFairs] = useState([]);

  // Öne çıkan fuarları çek
  useEffect(() => {
    fetch(`${API}/public/fairs?featured_only=true&limit=8`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => setFeaturedFairs(data.items || []))
      .catch(() => {});
  }, []);

  // When city/fair route params change, update state
  useEffect(() => {
    if (cityParam) setCity(decodeURIComponent(cityParam));
    if (fairSlug) setFair(fairSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
  }, [cityParam, fairSlug]);

  const fetchContractors = async (params = {}) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (params.service || service) p.set('service', params.service ?? service);
      if (params.city || city) p.set('city', params.city ?? city);
      if (params.fair || fair) p.set('fair', params.fair ?? fair);
      if (params.q || q) p.set('q', params.q ?? q);
      p.set('limit', '30');
      const res = await axios.get(`${API}/public/contractors?${p.toString()}`);
      setContractors(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      setContractors([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContractors();
  }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchContractors();
  };

  const handleSaveSearch = async () => {
    if (!user) { navigate('/contractor/login'); return; }
    const name = window.prompt('Bu aramaya bir ad verin:', q || service || city || 'Kaydedilen Arama');
    if (!name) return;
    try {
      await axios.post(`${API}/saved-searches`,
        { name, search_type: 'contractor', filters: { q, service, city, fair } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Arama kaydedildi!', { description: 'Kayıtlı Aramalar sayfasından erişebilirsiniz.' });
    } catch { toast.error('Arama kaydedilemedi'); }
  };

  const clearFilter = (field) => {
    if (field === 'service') { setService(''); fetchContractors({ service: '' }); }
    if (field === 'city') { setCity(''); fetchContractors({ city: '' }); }
    if (field === 'fair') { setFair(''); fetchContractors({ fair: '' }); }
  };

  const activeFilters = [
    service && { key: 'service', label: service },
    city && { key: 'city', label: `📍 ${city}` },
    fair && { key: 'fair', label: `🎪 ${fair}` },
  ].filter(Boolean);

  const ScoreBar = ({ score }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-400' : 'bg-gray-300'}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-xs font-bold text-gray-600 w-8 shrink-0">{score}%</span>
    </div>
  );

  const pageTitle = fair
    ? `${fair} Exhibition Stand Contractors | ExpoStand`
    : city
    ? `Exhibition Stand Contractors in ${city.charAt(0).toUpperCase() + city.slice(1)} | ExpoStand`
    : service
    ? `${service} Contractors | ExpoStand`
    : 'Find Exhibition Stand Contractors | ExpoStand';

  const pageDesc = fair
    ? `Find verified exhibition stand contractors for ${fair}. Get free quotes from multiple companies with experience at ${fair}.`
    : city
    ? `Find ${total > 0 ? total + ' ' : ''}verified exhibition stand contractors in ${city}. Compare profiles, services, and get free quotes.`
    : 'Find and compare verified exhibition stand contractors worldwide. View portfolios, check fair experience, and get free quotes.';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        {city && <link rel="canonical" href={`https://expostand.com/find-contractors/${city.toLowerCase()}`} />}
        {fair && <link rel="canonical" href={`https://expostand.com/find-contractors/fair/${(fair || '').toLowerCase().replace(/\s+/g, '-')}`} />}
      </Helmet>
      <PublicNavbar />

      {/* Hero Search Bar */}
      <div className="bg-gray-900 text-white py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Find Exhibition Stand Contractors</h1>
            <p className="text-gray-400 text-sm">
              {total > 0 ? `${total} contractors found` : 'Search from our verified contractor network'}
              {city && ` in ${city}`}
              {fair && ` for ${fair}`}
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={service}
                  onChange={e => setService(e.target.value)}
                  placeholder="Service (e.g. Stand Design)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                  list="service-suggestions"
                />
                <datalist id="service-suggestions">
                  {SERVICE_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="City (e.g. Frankfurt)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
              <div className="relative">
                <Star className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={fair}
                  onChange={e => setFair(e.target.value)}
                  placeholder="Fair (e.g. Hannover Messe)"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Search by company name..."
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-gray-400 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40"
                />
              </div>
              <button type="submit" className="px-8 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors text-sm shrink-0">
                Search
              </button>
            </div>
          </form>

          {/* Öne Çıkan Fuarlar */}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-gray-500">Looking for a supplier instead?</span>
            <button
              onClick={() => navigate(`/find-suppliers?product=${service}&city=${city}`)}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 font-medium"
            >
              Find Suppliers <ArrowRight className="h-3 w-3" />
            </button>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-amber-400 hidden sm:inline font-semibold">★ Öne Çıkan:</span>
              {(featuredFairs.length > 0 ? featuredFairs : [
                { name: 'Hannover Messe', fair_id: 'hannover-messe-slug' },
                { name: 'ISE Amsterdam', fair_id: 'ise-amsterdam-slug' },
                { name: 'Bauma Munich', fair_id: 'bauma-slug' },
                { name: 'MWC Barcelona', fair_id: 'mwc-slug' },
              ]).map(f => {
                const slug = f.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                const isActive = fair.toLowerCase() === f.name.toLowerCase();
                return (
                  <button key={f.fair_id || slug}
                    onClick={() => {
                      setFair(f.name);
                      fetchContractors({ fair: f.name });
                    }}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-all font-medium
                      ${isActive
                        ? 'bg-amber-400 text-gray-900 border-amber-400 shadow'
                        : 'text-amber-300 hover:text-white bg-white/10 hover:bg-amber-500/20 border-amber-400/40 hover:border-amber-400'
                      }`}
                  >
                    ★ {f.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-medium">Active filters:</span>
            {activeFilters.map(f => (
              <span key={f.key} className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-200">
                {f.label}
                <button onClick={() => clearFilter(f.key)} className="hover:text-blue-900 ml-0.5">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Öne Çıkan Fuarlar Bölümü — yalnızca fuar filtresi yoksa göster */}
      {!fair && featuredFairs.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100 px-4 py-5">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-amber-500 text-base">★</span>
              <h2 className="text-sm font-bold text-amber-900">Öne Çıkan Fuarlar</h2>
              <span className="text-xs text-amber-600/70">— Admin tarafından öne çıkarılan büyük organizasyonlar</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
              {featuredFairs.map(f => (
                <button
                  key={f.fair_id}
                  onClick={() => { setFair(f.name); fetchContractors({ fair: f.name }); }}
                  className="shrink-0 group flex flex-col items-start bg-white border border-amber-200 hover:border-amber-400 rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all min-w-[160px] max-w-[200px]"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-amber-400 text-xs">★</span>
                    <span className="text-[10px] font-black text-amber-500 uppercase tracking-wide">Öne Çıkan</span>
                  </div>
                  <p className="text-xs font-bold text-gray-900 leading-tight text-left group-hover:text-amber-700 transition-colors">{f.name}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{f.city}, {f.country}</p>
                  {f.visitors && f.visitors !== '—' && (
                    <p className="text-[10px] text-gray-400 mt-1">{f.visitors} ziyaretçi</p>
                  )}
                  <div className="mt-2 text-[10px] text-amber-600 font-semibold group-hover:underline">Contractor'ları gör →</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="flex-1 py-6 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 animate-pulse h-52 shadow-sm" />
              ))}
            </div>
          ) : contractors.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No contractors found</h3>
              <p className="text-gray-500 text-sm mb-4">Try adjusting your search criteria</p>
              <button onClick={() => { setService(''); setCity(''); setFair(''); setQ(''); fetchContractors({ service: '', city: '', fair: '', q: '' }); }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="text-sm text-gray-600 font-medium">{total} contractors found</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Save Search button */}
                  <button
                    onClick={handleSaveSearch}
                    className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-blue-600 border border-gray-200 hover:border-blue-300 bg-white hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid="save-search-btn"
                    title="Bu aramayı kaydet"
                  >
                    <Bookmark className="h-3.5 w-3.5" />Kaydet
                  </button>
                  {/* View toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'list' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <List className="h-3.5 w-3.5" /> List
                    </button>
                    <button
                      onClick={() => setViewMode('map')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'map' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <Map className="h-3.5 w-3.5" /> Map
                    </button>
                    <button
                      onClick={() => setViewMode('split')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'split' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <Filter className="h-3.5 w-3.5" /> Split
                    </button>
                  </div>
                  {!user && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5">
                      <Lock className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-xs text-amber-700 font-medium">Register to see contact details</span>
                      <Link to="/exhibitor/signup" className="text-xs text-blue-600 font-semibold hover:underline ml-1">Sign up free →</Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Split view: list + map side by side */}
              {viewMode === 'split' ? (
                <div className="flex gap-5">
                  <div className="w-full lg:w-1/2 space-y-4 overflow-y-auto" style={{ maxHeight: '620px' }}>
                    {contractors.map(c => (
                      <ContractorCard key={c.org_id} contractor={c} isLoggedIn={!!user} compact
                        compareList={compareList} setCompareList={setCompareList} />
                    ))}
                  </div>
                  <div className="hidden lg:block flex-1">
                    <Suspense fallback={<div className="h-full bg-gray-100 rounded-2xl animate-pulse" />}>
                      <SearchMap items={contractors} type="contractor" height="620px" />
                    </Suspense>
                  </div>
                </div>
              ) : viewMode === 'map' ? (
                <Suspense fallback={<div className="h-96 bg-gray-100 rounded-2xl animate-pulse" />}>
                  <SearchMap items={contractors} type="contractor" height="520px" />
                </Suspense>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {contractors.map(c => (
                    <ContractorCard key={c.org_id} contractor={c} isLoggedIn={!!user}
                      compareList={compareList} setCompareList={setCompareList} />
                  ))}
                </div>
              )}

              {/* Floating Compare Bar */}
              {compareList.length >= 2 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl border border-gray-700">
                  <BarChart2 className="h-4 w-4 text-blue-400" />
                  <span className="text-sm font-semibold">{compareList.length} selected</span>
                  <Link
                    to={`/compare-contractors?slugs=${compareList.join(',')}`}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-1.5 rounded-xl transition-colors"
                  >
                    Compare Now →
                  </Link>
                  <button onClick={() => setCompareList([])} className="text-gray-400 hover:text-white ml-1">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Sign-up CTA at bottom */}
              {!user && contractors.length > 0 && (
                <div className="mt-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
                  <Award className="h-10 w-10 mx-auto mb-3 opacity-90" />
                  <h3 className="text-xl font-bold mb-2">Get Free Quotes from These Contractors</h3>
                  <p className="text-blue-100 text-sm mb-5 max-w-md mx-auto">
                    Create a free brief and receive competitive quotes from multiple verified contractors within 24 hours.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/exhibitor/signup"
                      className="bg-white text-blue-700 font-bold px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors text-sm">
                      Create Free Account
                    </Link>
                    <Link to="/exhibitor/brief/new"
                      className="border border-white/40 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors text-sm">
                      Post a Brief
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}

function ContractorCard({ contractor: c, isLoggedIn, compact = false, compareList = [], setCompareList = () => {} }) {
  const [showContact, setShowContact] = useState(false);
  const isInCompare = compareList.includes(c.slug);
  const canAddMore = compareList.length < 3;

  const toggleCompare = () => {
    if (isInCompare) {
      setCompareList(prev => prev.filter(s => s !== c.slug));
    } else if (canAddMore) {
      setCompareList(prev => [...prev, c.slug]);
    }
  };

  if (compact) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-blue-100 transition-all group flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center flex-shrink-0 font-bold text-white text-sm">
          {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover rounded-lg" /> : c.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1 mb-1">
            <h3 className="font-bold text-gray-900 text-xs group-hover:text-blue-700 transition-colors line-clamp-1">{c.name}</h3>
            {c.is_featured && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold shrink-0">TOP</span>}
          </div>
          <p className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-0.5">
            <MapPin className="h-2.5 w-2.5" />{c.city && c.country ? `${c.city}, ${c.country}` : c.country || c.city || '—'}
          </p>
          <div className="flex items-center gap-1.5">
            <Link to={`/contractor/${c.slug}`} className="text-[10px] font-semibold text-blue-600 hover:underline">View →</Link>
            <span className="text-gray-200">|</span>
            <span className="text-[10px] text-gray-400">{c.profile_score}% score</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-sm border transition-all group overflow-hidden relative ${isInCompare ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-100 hover:shadow-md hover:border-blue-100'}`}>
      {/* Compare checkbox — top-right corner */}
      <button
        onClick={toggleCompare}
        title={isInCompare ? 'Remove from comparison' : canAddMore ? 'Add to comparison (max 3)' : 'Max 3 selected'}
        disabled={!canAddMore && !isInCompare}
        className={`absolute top-3 right-3 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${isInCompare ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white hover:border-blue-400'} ${!canAddMore && !isInCompare ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {isInCompare && <Check className="h-3 w-3 text-white" />}
      </button>
      {/* Header */}
      <div className="p-5 pb-4 pr-10">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-sm">
            {c.logo_url ? (
              <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover rounded-xl" />
            ) : (
              c.name.charAt(0)
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1">
              <h3 className="font-bold text-gray-900 text-sm leading-tight group-hover:text-blue-700 transition-colors line-clamp-2">
                {c.name}
              </h3>
              <div className="flex items-center gap-1 shrink-0">
                {c.is_verified && (
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold border border-emerald-200">
                    ✓ VER
                  </span>
                )}
                {c.is_featured && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold border border-amber-200">
                    ⭐ TOP
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
              <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
              {c.city && c.country ? `${c.city}, ${c.country}` : c.country || c.city || '—'}
            </p>
          </div>
        </div>

        {c.tagline && (
          <p className="text-xs text-gray-500 italic line-clamp-2 mb-3">{c.tagline}</p>
        )}

        {/* Services */}
        <div className="flex flex-wrap gap-1 mb-3">
          {(c.services || []).slice(0, 3).map((s, i) => (
            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium border border-blue-100">
              {s}
            </span>
          ))}
          {(c.services || []).length > 3 && (
            <span className="text-[10px] text-gray-400 px-1">+{c.services.length - 3} more</span>
          )}
        </div>

        {/* Score bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-400 font-medium">Profile Score</span>
            <span className="text-[10px] text-gray-500">{c.proposal_count} proposals sent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${c.profile_score >= 80 ? 'bg-emerald-500' : c.profile_score >= 60 ? 'bg-amber-400' : 'bg-gray-300'}`}
                style={{ width: `${c.profile_score}%` }}
              />
            </div>
            <span className="text-[10px] font-bold text-gray-600 w-7 shrink-0">{c.profile_score}%</span>
          </div>
        </div>

        {/* Coverage */}
        {c.coverage_count > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-gray-400">
            <Globe className="h-3 w-3" />
            <span>Covers {c.coverage_count} {c.coverage_count === 1 ? 'country' : 'countries'}</span>
          </div>
        )}

        {/* Active Shows badges */}
        {c.active_shows?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {c.active_shows.slice(0, 2).map((show, i) => (
              <span key={i} className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                ⭐ {show}
              </span>
            ))}
            {c.active_shows.length > 2 && (
              <span className="text-[9px] text-gray-400">+{c.active_shows.length - 2} shows</span>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-50 p-3 flex items-center gap-2">
        <Link
          to={`/contractor/${c.slug}`}
          className="flex-1 text-center text-xs font-semibold text-blue-600 hover:text-blue-700 py-2 rounded-lg hover:bg-blue-50 transition-colors"
        >
          View Profile
        </Link>
        {isLoggedIn ? (
          <Link
            to={`/app/exhibitor/find-contractors`}
            className="flex-1 text-center text-xs font-semibold bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Invite to Brief
          </Link>
        ) : (
          <Link
            to="/exhibitor/signup"
            className="flex-1 text-center text-xs font-semibold bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-1"
          >
            <Lock className="h-3 w-3" /> Contact
          </Link>
        )}
      </div>
    </div>
  );
}
